<template>
  <b-card class="bulk-treatment-import-form">
    <h5 class="mb-4">
      <i class="fas fa-teeth mr-2 text-primary"></i>
      Bulk Treatment Import
      <small class="text-muted d-block mt-1">
        ไฟล์ ProcedureCode จากโปรแกรมเดิม
      </small>
    </h5>

    <!-- Step 1: pick the target and the file, then dry run to read its sections -->
    <b-form v-if="!sections.length" @submit.prevent="handleDryRun">
      <ClinicSearchSelect
        v-model="selectedClinic"
        label="Select Clinic *"
        :state="$v.selectedClinic.$dirty ? !$v.selectedClinic.$error : null"
      />
      <b-form-invalid-feedback :state="!$v.selectedClinic.$error">
        Please select a clinic
      </b-form-invalid-feedback>

      <b-form-group label="Select Branch *" class="mt-3">
        <b-form-select
          v-model="selectedBranchId"
          :options="branchOptions"
          :disabled="!selectedClinic"
          :state="$v.selectedBranchId.$dirty ? !$v.selectedBranchId.$error : null"
          @change="$v.selectedBranchId.$touch()"
        >
          <template #first>
            <b-form-select-option :value="null" disabled>
              {{ selectedClinic ? 'Choose a branch...' : 'Select a clinic first' }}
            </b-form-select-option>
          </template>
        </b-form-select>
        <b-form-invalid-feedback :state="!$v.selectedBranchId.$error">
          Please select a branch
        </b-form-invalid-feedback>
      </b-form-group>

      <ExcelFileUploader
        v-model="file"
        label="Upload ProcedureCode *"
        :state="$v.file.$dirty ? !$v.file.$error : null"
        class="mt-3"
        @input="$v.file.$touch()"
      />
      <b-form-invalid-feedback :state="!$v.file.$error">
        File is required
      </b-form-invalid-feedback>

      <b-button type="submit" variant="primary" :disabled="loading" block class="mt-3">
        <i class="fas mr-2" :class="loading ? 'fa-spinner fa-spin' : 'fa-search'"></i>
        {{ loading ? 'Reading file...' : 'อ่านไฟล์และแสดงหมวด' }}
      </b-button>
    </b-form>

    <!-- Step 2: map every section in the file onto a branch category -->
    <div v-else>
      <b-alert show :variant="unmappedCount ? 'warning' : 'success'" class="mb-3">
        <i class="fas mr-2" :class="unmappedCount ? 'fa-exclamation-triangle' : 'fa-check'"></i>
        <template v-if="unmappedCount">
          ยังไม่ได้เลือกหมวดให้ {{ unmappedCount }} หมวด
          — treatments in those sections will not be imported until you choose.
        </template>
        <template v-else>
          ครบทุกหมวดแล้ว — {{ importableCount }} treatments พร้อมนำเข้า
        </template>
      </b-alert>

      <b-table-simple small responsive hover class="mb-3">
        <b-thead>
          <b-tr>
            <b-th>หมวดในไฟล์</b-th>
            <b-th class="text-right" style="width: 6rem;">รายการ</b-th>
            <b-th style="width: 16rem;">นำเข้าเป็นหมวด</b-th>
          </b-tr>
        </b-thead>
        <b-tbody>
          <b-tr
            v-for="section in sections"
            :key="section.section"
            :class="{ 'table-warning': assignments[section.section] === undefined }"
          >
            <b-td>{{ section.section }}</b-td>
            <b-td class="text-right">{{ section.count }}</b-td>
            <b-td>
              <b-form-select
                v-model="assignments[section.section]"
                :options="categoryOptions"
                size="sm"
              >
                <template #first>
                  <b-form-select-option :value="undefined" disabled>
                    เลือกหมวด...
                  </b-form-select-option>
                </template>
              </b-form-select>
            </b-td>
          </b-tr>
        </b-tbody>
      </b-table-simple>

      <p class="small text-muted">
        เลือก <strong>ข้ามหมวดนี้</strong> สำหรับหมวดที่ไม่ใช่รายการรักษา เช่น
        รหัสยกเลิกนัด หรือ Clinical Note. หมวดที่ยังไม่มีในสาขาจะถูกสร้างให้อัตโนมัติ.
      </p>

      <b-form-group
        label="DF (%)"
        description="ไฟล์นี้ไม่มีค่า DF ทุกรายการที่นำเข้าจะถูกตั้งเป็น % ของราคาตามค่านี้ แก้ไขภายหลังได้"
        class="mb-3"
        style="max-width: 12rem;"
      >
        <b-form-input v-model="df" type="number" min="0" max="100" step="1" size="sm" />
      </b-form-group>

      <b-button variant="outline-secondary" class="mr-2" :disabled="loading" @click="resetForm">
        <i class="fas fa-arrow-left mr-2"></i>เริ่มใหม่
      </b-button>
      <b-button variant="primary" :disabled="loading || unmappedCount > 0" @click="handleImport">
        <i class="fas mr-2" :class="loading ? 'fa-spinner fa-spin' : 'fa-file-import'"></i>
        {{ loading ? 'Importing...' : `นำเข้า ${importableCount} รายการ` }}
      </b-button>
    </div>
  </b-card>
</template>

<script>
import { required } from 'vuelidate/lib/validators';
import { mapGetters, mapActions } from 'vuex';
import ExcelFileUploader from './ExcelFileUploader.vue';
import ClinicSearchSelect from './ClinicSearchSelect.vue';

// A section mapped to null is skipped on purpose rather than left unmapped.
const SKIP_VALUE = null;

export default {
  name: 'BulkTreatmentImportForm',
  components: { ExcelFileUploader, ClinicSearchSelect },
  data() {
    return {
      selectedClinic: null,
      selectedBranchId: null,
      file: null,
      sections: [],
      availableCategories: [],
      assignments: {},
      // The legacy file has no DF column; every imported treatment gets this
      // percentage. 50 matches the API default.
      df: 50
    };
  },
  validations: {
    selectedClinic: { required },
    selectedBranchId: { required },
    file: { required }
  },
  computed: {
    ...mapGetters('moduleSuperadmin', ['isImportTreatmentsLoading']),
    loading() {
      return this.isImportTreatmentsLoading;
    },
    branchOptions() {
      if (!this.selectedClinic || !this.selectedClinic.branches) return [];
      return this.selectedClinic.branches.map(branch => ({
        value: branch.branch_id || branch.branchId,
        text: `${branch.branch_code || branch.branchCode} - ${branch.branch_name_th || branch.branchNameTh}`
      }));
    },
    categoryOptions() {
      return [
        { value: SKIP_VALUE, text: 'ข้ามหมวดนี้ (ไม่ใช่รายการรักษา)' },
        ...this.availableCategories.map(cate => ({
          value: cate.nameTh,
          text: cate.nameTh
        }))
      ];
    },
    unmappedCount() {
      return this.sections.filter(s => this.assignments[s.section] === undefined).length;
    },
    importableCount() {
      return this.sections
        .filter(s => this.assignments[s.section] !== undefined
          && this.assignments[s.section] !== SKIP_VALUE)
        .reduce((total, s) => total + s.count, 0);
    }
  },
  watch: {
    selectedClinic() {
      this.selectedBranchId = null;
    }
  },
  methods: {
    ...mapActions('moduleSuperadmin', ['importTreatments']),
    buildFormData(extra = {}) {
      const formData = new FormData();
      formData.append('file', this.file);
      formData.append('clinicId', String(this.selectedClinic.clinicId || this.selectedClinic.id));
      formData.append('branchId', String(this.selectedBranchId));
      Object.entries(extra).forEach(([key, value]) => formData.append(key, value));
      return formData;
    },
    async handleDryRun() {
      this.$v.$touch();
      if (this.$v.$invalid) return;
      try {
        const result = await this.importTreatments(this.buildFormData({ dryRun: 'true' }));
        this.sections = result.data.sections || [];
        this.availableCategories = result.data.availableCategories || [];
        // Every section gets a key up front: Vue 2 cannot make a property
        // reactive that was not present when the object was assigned, so an
        // absent key would leave its dropdown permanently unresponsive.
        // Unmapped sections start undefined so the operator has to choose.
        this.assignments = this.sections.reduce((acc, section) => {
          acc[section.section] = section.unmapped ? undefined : section.target;
          return acc;
        }, {});
      } catch (error) {
        this.toastError(error, 'Failed to read the file');
      }
    },
    async handleImport() {
      const categoryMap = this.sections.reduce((acc, section) => {
        acc[section.section] = this.assignments[section.section];
        return acc;
      }, {});
      try {
        const result = await this.importTreatments(
          this.buildFormData({
            categoryMap: JSON.stringify(categoryMap),
            df: String(this.df)
          })
        );
        this.$emit('success', result.data);
        this.resetForm();
      } catch (error) {
        this.toastError(error, 'Failed to import treatments');
      }
    },
    toastError(error, fallback) {
      const message = error.response?.data?.error || fallback;
      this.$bvToast.toast(message, {
        title: 'Error',
        variant: 'danger',
        autoHideDelay: 5000
      });
    },
    resetForm() {
      this.selectedClinic = null;
      this.selectedBranchId = null;
      this.file = null;
      this.sections = [];
      this.availableCategories = [];
      this.assignments = {};
      this.df = 50;
      this.$v.$reset();
    }
  }
};
</script>
